import type { SSEMessage } from '@/_shared/types';

export function applySSEEvent(
  prevMessages: SSEMessage[],
  eventType: string,
  data: Record<string, unknown>,
): SSEMessage[] {
  // Defense-in-depth: if the event carries a seq and we already have a message
  // with that seq, skip the append — this catches any upstream dedup failure.
  const seq = data['seq'] as number | undefined;
  if (seq != null && prevMessages.some((m) => m.seq === seq)) {
    return prevMessages;
  }

  switch (eventType) {
    case 'user_message': {
      const content = data['content'] as string;
      // Dedup: scan backward for a seq-less (optimistic) user_message with the
      // same content. Only seq-less messages are candidates — messages with seq
      // are persisted and canonical, so matching content across turns is
      // coincidence, not duplication.
      let turnStartsSeen = 0;
      for (let i = prevMessages.length - 1; i >= 0; i--) {
        const m = prevMessages[i]!;
        // Stop when we cross into a previous turn — the first turn_start is
        // our own turn, the second means we've left the current turn scope.
        if (m.type === 'turn_start') {
          turnStartsSeen++;
          if (turnStartsSeen >= 2) break;
          continue;
        }
        if (m.type === 'user_message' && m.seq == null && m.content === content) {
          if (seq != null) {
            // Upgrade optimistic (seq-less) message with the real seq number.
            const updated = [...prevMessages];
            updated[i] = { ...m, seq };
            return updated;
          }
          // Both seq-less with same content — exact duplicate echo, skip.
          return prevMessages;
        }
      }
      return [...prevMessages, { type: 'user_message', content, seq }];
    }

    case 'assistant':
      return [...prevMessages, { type: 'assistant', content: data['content'] as string, seq }];

    case 'tool_use':
      return [
        ...prevMessages,
        {
          type: 'tool_use',
          name: data['name'] as string,
          friendlyName: data['friendlyName'] as string,
          category: data['category'] as string,
          action: data['action'] as string,
          args: data['args'],
          id: data['id'] as string,
          summary: data['summary'] as string | undefined,
          groupedTitle: data['groupedTitle'] as string | undefined,
          detail:
            (data['detail'] as { type: 'code'; content: string; language: string } | null | undefined) ?? undefined,
          seq,
        },
      ];

    case 'tool_result':
      return [
        ...prevMessages,
        {
          type: 'tool_result',
          id: data['id'] as string,
          content: data['content'] as string,
          seq,
        },
      ];

    case 'ui_message':
      return [
        ...prevMessages,
        {
          type: 'ui_message',
          uiType: data['uiType'] as
            | 'text'
            | 'table'
            | 'code'
            | 'alert'
            | 'chart'
            | 'recommend_actions'
            | 'change_title'
            | 'measurement_plan',
          content: data['content'] as string,
          metadata: data['metadata'],
          seq,
        },
      ];

    case 'result':
      return [
        ...prevMessages,
        {
          type: 'result',
          costUsd: data['costUsd'] as number,
          turns: data['turns'] as number,
          durationMs: data['durationMs'] as number,
          seq,
        },
      ];

    case 'turn_start':
      return [
        ...prevMessages,
        {
          type: 'turn_start',
          turnId: data['turnId'] as string,
          startedAt: data['startedAt'] as number,
          seq,
        },
      ];

    case 'turn_complete':
      return [...prevMessages, { type: 'turn_complete', turnId: data['turnId'] as string, seq }];

    case 'skill_invoked':
      return [
        ...prevMessages,
        {
          type: 'skill_invoked',
          skillId: data['skillId'] as string,
          title: data['title'] as string,
          seq,
        },
      ];

    case 'memory_flushed':
      return [
        ...prevMessages,
        {
          type: 'memory_flushed',
          count: data['count'] as number,
          ids: data['ids'] as string[],
          seq,
        },
      ];

    case 'compaction_started':
      return [
        ...prevMessages,
        {
          type: 'compaction_started',
          trigger: data['trigger'] as 'auto' | 'manual',
          timestamp: data['timestamp'] as number,
          seq,
        },
      ];

    case 'turn_aborted': {
      const turnId = data['turnId'] as string;
      const startIdx = prevMessages.findIndex((m) => m.type === 'turn_start' && m.turnId === turnId);
      if (startIdx === -1) return prevMessages;
      const userMessage = typeof data['userMessage'] === 'string' ? data['userMessage'] : '';
      // If the message immediately before turn_start matches the aborted prompt,
      // it's the dashboard sidebar's optimistic (or upgraded optimistic) — drop it too.
      const prev = prevMessages[startIdx - 1];
      const cutAt =
        prev?.type === 'user_message' && userMessage && prev.content === userMessage ? startIdx - 1 : startIdx + 1;
      // Keep up to cutAt; append the abort marker so sliceIntoTurns can mark
      // the slice 'aborted' and consumers can unlock the composer.
      return [
        ...prevMessages.slice(0, cutAt),
        {
          type: 'turn_aborted',
          turnId,
          userMessage,
          seq: data['seq'] as number | undefined,
        },
      ];
    }

    case 'error':
      return [...prevMessages, { type: 'error', message: data['message'] as string, seq }];

    default:
      return prevMessages;
  }
}
